import { CustomScriptPlacement, CustomScripts } from "@/cms/models";

/**
 * Helpers for the third-party scripts a website administrator manages in the
 * Scripts tab in b2b.
 *
 * The b2b side stores each snippet base64-encoded inside the homepage settings
 * payload, so the JSON stays valid no matter what HTML/JS is pasted. These
 * helpers are shared by the layout (a server component, for the enabled check)
 * and by CustomScripts (a client component, for the decode), so nothing here may
 * import server-only modules or touch document/window at module scope.
 */

// Base64 alphabet plus optional padding. Checked together with a re-encode
// round-trip below, because the charset alone also matches plain text.
const BASE64_CHARSET = /^[A-Za-z0-9+/]+={0,2}$/;

function isBase64(value: string): boolean {
  if (value.length === 0 || value.length % 4 !== 0) {
    return false;
  }
  if (!BASE64_CHARSET.test(value)) {
    return false;
  }
  try {
    // atob/btoa are byte-exact inverses, so a real base64 string re-encodes to
    // itself. Anything else (for example hand-written code) fails here.
    const binary = atob(value);
    if (btoa(binary) !== value) {
      return false;
    }

    // The byte round-trip alone still accepts plain tokens that happen to use the
    // base64 charset with a length divisible by four - a hex verification token, a
    // numeric pixel id, or a word like "code". b2b applies the same UTF-8
    // round-trip, so requiring it here keeps both sides showing and injecting the
    // same thing: such a token is not valid UTF-8 and stays plain code.
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const reEncoded = new TextEncoder().encode(new TextDecoder().decode(bytes));

    return (
      reEncoded.length === bytes.length &&
      bytes.every((byte, index) => byte === reEncoded[index])
    );
  } catch {
    return false;
  }
}

/**
 * Decode a stored snippet back to source. Deliberately tolerant: someone may
 * hand-edit plain code into the advanced JSON editor, and that must render as
 * the code they wrote rather than as garbage, so a value that is not valid
 * base64 is returned unchanged. Never throws.
 *
 * Decoding goes through TextDecoder because atob alone is latin1 and would
 * corrupt Danish characters (æøå/ÆØÅ) in the snippet.
 */
export function decodeCustomScript(encoded: string | undefined | null): string {
  if (typeof encoded !== "string" || encoded.trim().length === 0) {
    return "";
  }

  const candidate = encoded.trim();
  if (!isBase64(candidate)) {
    return encoded;
  }

  try {
    const binary = atob(candidate);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder().decode(bytes);
  } catch {
    return encoded;
  }
}

/**
 * The still-encoded code for a placement, but only when it is actually meant to
 * load: the placement exists, it is activated, and it holds real code.
 * Deactivating a script keeps its code but stops it from being injected.
 */
export function getEnabledScript(
  scripts: CustomScripts | undefined,
  placement: CustomScriptPlacement,
): string | null {
  const script = scripts?.[placement];
  if (!script || script.enabled !== true) {
    return null;
  }
  if (typeof script.code !== "string" || script.code.trim().length === 0) {
    return null;
  }

  return script.code;
}
